import supplierApi from '../services/supplierApi';

const SUPPLIER_BASE_URL = '/api/supplier';

// Helper function to get supplier token from localStorage
const getSupplierToken = () => {
  return localStorage.getItem('supplier_token');
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const token = getSupplierToken();
  if (!token) {
    throw new Error('No supplier authentication token found');
  }

  const config = {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  };

  return supplierApi.request({
    url: endpoint,
    ...config
  });
};

export const supplierModel = {
  // Get supplier dashboard data
  getDashboardData: async () => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/dashboard`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier dashboard data:', error);
      throw error;
    }
  },

  // Get supplier invitations (RFQs)
  getInvitations: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.pageSize) queryParams.append('limit', params.pageSize);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const url = queryParams.toString() ? 
        `${SUPPLIER_BASE_URL}/invitations?${queryParams}` : 
        `${SUPPLIER_BASE_URL}/invitations`;
        
      const response = await supplierApi.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier invitations:', error);
      throw error;
    }
  },

  // Get supplier quotations
  getQuotations: async (params = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.pageSize) queryParams.append('limit', params.pageSize);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const url = `${SUPPLIER_BASE_URL}/quotations${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await supplierApi.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier quotations:', error);
      throw error;
    }
  },

  // Get supplier orders
  getOrders: async () => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/orders`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier orders:', error);
      throw error;
    }
  },

  // Get specific order details
  getOrder: async (orderId) => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/order/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  },

  // Get supplier details
  getSupplierDetails: async (supplierCode) => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/details/${supplierCode}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier details:', error);
      throw error;
    }
  },

  // Update supplier address
  updateAddress: async (addressData) => {
    try {
      console.log('Sending address update request to:', `${SUPPLIER_BASE_URL}/update-address`);
      console.log('Address data being sent:', addressData);
      
      // Convert to FormData since PHP expects $_POST data
      const formData = new FormData();
      formData.append('address', addressData.address || '');
      formData.append('phone', addressData.phone || '');
      formData.append('tel', addressData.tel || '');
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/update-address`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Address update API response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier address:', error);
      console.error('Request URL:', `${SUPPLIER_BASE_URL}/update-address`);
      console.error('Request data:', addressData);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Update company details (only allowed for unapproved suppliers)
  updateCompanyDetails: async (companyData) => {
    try {
      console.log('Sending company details update request to:', `${SUPPLIER_BASE_URL}/update-company`);
      console.log('Company data being sent:', companyData);
      
      // Convert to FormData since PHP expects $_POST data
      const formData = new FormData();
      formData.append('BP_Name', companyData.BP_Name || '');
      formData.append('Company_Registered_Name', companyData.Company_Registered_Name || '');
      formData.append('Contact_Person', companyData.Contact_Person || '');
      formData.append('Company_Reg', companyData.Company_Reg || '');
      formData.append('Vat_Registration', companyData.Vat_Registration || '');
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/update-company`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Company details update API response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating company details:', error);
      console.error('Request URL:', `${SUPPLIER_BASE_URL}/update-company`);
      console.error('Request data:', companyData);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Upload document
  uploadDocument: async (formData) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // Download file
  downloadFile: async (filePath, fileName) => {
    try {
      const response = await supplierApi.post('/files/download', {
        dl: filePath,
        name: fileName,
        token: getSupplierToken()
      }, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },

  // Get RFQ documents
  getRFQDocument: async (rfqRef, supplierCode, supplierName) => {
    try {
      const url = `/documents/rfq/${rfqRef}?SID=${encodeURIComponent(supplierCode)}&S=${encodeURIComponent(supplierName)}`;
      window.open(url, '_blank');
      return { success: true };
    } catch (error) {
      console.error('Error opening RFQ document:', error);
      throw error;
    }
  },

  // Submit quotation
  submitQuotation: async (quotationData) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/quotation/submit`, quotationData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting quotation:', error);
      throw error;
    }
  },

  // Get quotation details
  getQuotationDetails: async (quotationId) => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/quotation/${quotationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation details:', error);
      throw error;
    }
  },

  // Setup supplier user profile (name, position, password)
  setupProfile: async (profileData) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/save_profile`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error setting up supplier profile:', error);
      throw error;
    }
  },

  // Update supplier user profile (name, position, and optionally password)
  updateProfile: async (profileData) => {
    try {
      const formData = new FormData();
      formData.append('name', profileData.name);
      formData.append('position', profileData.position);
      
      // Only include password if provided
      if (profileData.password) {
        formData.append('password', profileData.password);
      }
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/update-profile`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating supplier profile:', error);
      throw error;
    }
  },

  // Add email address for supplier
  addEmail: async (emailData) => {
    try {
      const formData = new FormData();
      formData.append('email', emailData.email);
      formData.append('can_login', emailData.canLogin ? '1' : '0');
      formData.append('view_only', emailData.viewOnly ? '1' : '0');
      formData.append('send_link', emailData.sendLink ? '1' : '0');
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/add`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error adding email:', error);
      throw error;
    }
  },

  // Update email settings for supplier
  updateEmail: async (emailData) => {
    try {
      const formData = new FormData();
      formData.append('email', emailData.email);
      formData.append('current_email', emailData.current_email);
      formData.append('can_login', emailData.canLogin ? '1' : '0');
      formData.append('view_only', emailData.viewOnly ? '1' : '0');
      formData.append('send_link', emailData.sendLink ? '1' : '0');
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/update`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  },

  // Helpdesk methods - Enterprise Helpdesk Integration
  getHelpdeskTickets: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.pageSize) queryParams.append('limit', params.pageSize);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);

      const url = queryParams.toString() ? 
        `${SUPPLIER_BASE_URL}/helpdesk/tickets?${queryParams}` : 
        `${SUPPLIER_BASE_URL}/helpdesk/tickets`;
        
      const response = await supplierApi.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier helpdesk tickets:', error);
      throw error;
    }
  },

  createHelpdeskTicket: async (ticketData) => {
    try {
      // If we have attachments, use FormData; otherwise use JSON
      let requestData = ticketData;
      let config = {};
      
      if (ticketData.attachments && ticketData.attachments.length > 0) {
        const formData = new FormData();
        
        // Add text fields
        formData.append('title', ticketData.title);
        formData.append('description', ticketData.description);
        formData.append('priority', ticketData.priority);
        formData.append('type', ticketData.type);
        
        // Add files
        ticketData.attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
        
        requestData = formData;
        config.headers = {
          'Content-Type': 'multipart/form-data'
        };
      }
      
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/helpdesk/tickets`, requestData, config);
      return response.data;
    } catch (error) {
      console.error('Error creating supplier helpdesk ticket:', error);
      throw error;
    }
  },

  getHelpdeskTicket: async (ticketId) => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/helpdesk/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier helpdesk ticket:', error);
      throw error;
    }
  },

  addHelpdeskTicketComment: async (ticketId, comment) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/helpdesk/tickets/${ticketId}/comments`, { 
        content: comment,
        comment: comment // Include both for backward compatibility
      });
      return response.data;
    } catch (error) {
      console.error('Error adding comment to supplier helpdesk ticket:', error);
      throw error;
    }
  },

  // Validate password setup token
  validatePasswordToken: async (token) => {
    try {
      const response = await supplierApi.get(`${SUPPLIER_BASE_URL}/validate-token/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error validating password token:', error);
      throw error;
    }
  },

  // Setup password for supplier user
  setupPassword: async (data) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/setup-password`, data);
      return response.data;
    } catch (error) {
      console.error('Error setting up password:', error);
      throw error;
    }
  },

  // Submit supplier documents
  submitDocuments: async (formData) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting documents:', error);
      throw error;
    }
  },

  // Get encrypted download parameters for quotation PDF
  getQuotationDownloadParams: async (params) => {
    try {
      const response = await supplierApi.post(`${SUPPLIER_BASE_URL}/quotation/download-params`, params);
      return response.data;
    } catch (error) {
      console.error('Error getting download parameters:', error);
      throw error;
    }
  }
};

export default supplierModel;
